// [PATCH onboarding-wizard v1] 16.07.2026 — 5 korakov: davčna+Poišči (korak 1),
// čist izbirnik programa (korak 2), razbitje šifranti (3) / knjižbe (4) s Kopiraj SQL,
// rezultat (5). Projekti vključen v Opal šifrante. Backend: [PATCH opal-projekti v1].
// [PATCH onboarding-wizard v1.1] Poišči napolni tudi opis dejavnosti (skd_description);
// ob zavezancu tiho nastavi vat_regime='Splošni'.
// [PATCH onboarding-wizard v1.2] vat_status poravnan na 'Zavezanec'/'Ni zavezanec' (ujema profilni dropdown).
// [PATCH onboarding-wizard v1.3] kartica Projekti: glavna = vstavljeni+oživljeni; chipi oživljenih/že aktivnih.
import { useEffect, useState } from 'react';  // [PATCH servis-profil v1]
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Search, Copy } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';  // [PATCH onboarding-veje v1]
import { OpalOnboardingDialog } from '../components/opal/OpalOnboardingDialog';  // [PATCH opal-onboarding-E v1]
import { VascoOnboardingDialog } from '../components/vasco/VascoOnboardingDialog';   // [PATCH vasco-sync-fe v1]

type Program = 'vasco' | 'pantheon' | 'opal';
type Skupina = 'sifranti' | 'knjizbe';

interface DatotekaSlot {
  kljuc: string;
  label: string;
  opis: string;
  skupina: Skupina;
  sql?: string;        // samo Opal — gumb "Kopiraj SQL"
  file: File | null;
}

// tekoče leto za Opal letno tabelo Y{leto}Konto
const LETO = new Date().getFullYear();

// Opal (OpPIS) — gole SQL poizvedbe (Opal-safe: brez ';' in brez '--')
const OPAL_SQL: Record<string, string> = {
  partnerji: `SELECT Sifra, Naziv, Naziv2Rac, NaslovRac, PostaRac, KrajRac, DrzavaRac,
       DavcStev, TaxNumber, DDVZavez, MaticnaSt, Dejavnost, Sektor,
       EMail, RacEmail, Trg, Status, DatumZaprt, KnjSkKup, KnjSkDob, NasTRR
FROM Partnerji ORDER BY Sifra`,
  konti: `SELECT * FROM Y${LETO}Konto ORDER BY 1`,
  sm: `SELECT * FROM StrMesta`,
  projekti: `SELECT Sifra, Naziv, Veljaven FROM Projekti ORDER BY Sifra`,
  trr: `SELECT * FROM TransRac ORDER BY Partner, Status`,
  vrste_knj: `SELECT * FROM VrsteKnj ORDER BY 1`,
  glave_pr: `SELECT ZapSt, IntStRac, OrgStRac, Dobavitelj, DobavNaz, DatPrejema, DatOrgRac,
       DatDUR, DatZapad, DatDDV, BilObd, Konto, ZnesekRac, Valuta, Tecaj,
       DDVKlas, DDVStatus, Trg, Samoobdav, VrstaProm, StrMesto, Leto, GlobDocID
FROM GlavePRvFA ORDER BY ZapSt`,
  pozicije_pr: `SELECT Link, ZapSt, IntStRac, VK, Konto, Stran, ZnesekVSIT, Osnova, Stopnja, DDV,
       StopOdbDel, KtoNeodPrh, DejKontDDV, DavcNepStr, ND, NDOsnCela, NDDDVCel,
       ISDDV, CrpanAvans, StrMesto, Projekt, BilObd, DatDDV
FROM PozPRvFA ORDER BY Link, ZapSt`, // [PATCH projekt-znanje v1]
  glave_ir: `SELECT * FROM GlaveIRvFA ORDER BY 1`,
  pozicije_ir: `SELECT * FROM PozIRvFA ORDER BY 1`,
};

// Vasco — ročni Excel izvozi (brez SQL). Projektov ni (Vasco vir ni definiran).
const VASCO_SLOTI: Omit<DatotekaSlot, 'file'>[] = [
  { kljuc: 'partnerji', label: 'Partnerji', opis: 'PARTNERJI.xlsx', skupina: 'sifranti' },
  { kljuc: 'konti', label: 'Kontni plan', opis: 'KONTO.xlsx', skupina: 'sifranti' },
  { kljuc: 'sm', label: 'Stroškovna mesta', opis: 'SM.xlsx', skupina: 'sifranti' },
  { kljuc: 'simboli', label: 'Simboli', opis: 'SIMBOL.xlsx', skupina: 'sifranti' },
  { kljuc: 'vrsta_knjige', label: 'Vrste knjiženja (DDV)', opis: 'VRSTA_KNJIGE_DDV.xlsx', skupina: 'sifranti' },
  { kljuc: 'knjizbe', label: 'Knjižbe', opis: 'KNJIŽBE.xls', skupina: 'knjizbe' },
  { kljuc: 'odbitek_ddv', label: 'Odbitek DDV', opis: 'IZPIS ODBITKA DDV.xls', skupina: 'knjizbe' },
];

// Opal — vrstni red šifrantov: Partnerji, Kontni plan, Stroškovna mesta, Projekti, TRR, Vrste knjiženja
const OPAL_SLOTI: Omit<DatotekaSlot, 'file'>[] = [
  { kljuc: 'partnerji', label: 'Partnerji', opis: 'šifrant partnerjev', skupina: 'sifranti', sql: OPAL_SQL.partnerji },
  { kljuc: 'konti', label: 'Kontni plan', opis: `kontni plan (${LETO})`, skupina: 'sifranti', sql: OPAL_SQL.konti },
  { kljuc: 'sm', label: 'Stroškovna mesta', opis: 'stroškovna mesta', skupina: 'sifranti', sql: OPAL_SQL.sm },
  { kljuc: 'projekti', label: 'Projekti', opis: 'projekti (Analitika)', skupina: 'sifranti', sql: OPAL_SQL.projekti },
  { kljuc: 'trr', label: 'TRR partnerjev', opis: 'transakcijski računi', skupina: 'sifranti', sql: OPAL_SQL.trr },
  { kljuc: 'vrste_knj', label: 'Vrste knjiženja', opis: 'vrste knjiženja (DDV)', skupina: 'sifranti', sql: OPAL_SQL.vrste_knj },
  { kljuc: 'glave_pr', label: 'Prejeti — glave', opis: 'prejeti računi — glave', skupina: 'knjizbe', sql: OPAL_SQL.glave_pr },
  { kljuc: 'pozicije_pr', label: 'Prejeti — pozicije', opis: 'prejeti računi — pozicije DDV', skupina: 'knjizbe', sql: OPAL_SQL.pozicije_pr },
  { kljuc: 'glave_ir', label: 'Izdani — glave', opis: 'izdani računi — glave', skupina: 'knjizbe', sql: OPAL_SQL.glave_ir },
  { kljuc: 'pozicije_ir', label: 'Izdani — pozicije', opis: 'izdani računi — pozicije DDV', skupina: 'knjizbe', sql: OPAL_SQL.pozicije_ir },
];

const SLOTI_ZA_PROGRAM: Record<Program, Omit<DatotekaSlot, 'file'>[]> = {
  vasco: VASCO_SLOTI,
  pantheon: VASCO_SLOTI,
  opal: OPAL_SLOTI,
};

const KORAKI = [1, 2, 3, 4, 5];

const inputCls =
  'w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2 placeholder-gray-400 dark:placeholder-gray-500';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const obstojeciCompanyId = searchParams.get('companyId');
  // [PATCH pregled-vstop v1] ?veja=pregled -> vstop naravnost na pregled stranke
  // za OBSTOJECE podjetje (gumb na Profilu podjetja).
  const zeljenaVeja = searchParams.get('veja');
  // korak 2 SAMO, kadar veja ni 'pregled': blok {korak === 2 &&} ni
  // pogojen z vejo, zato bi se sicer izrisala oba hkrati.
  const [korak, setKorak] = useState(
    obstojeciCompanyId && zeljenaVeja !== 'pregled' ? 2 : 1);

  // [PATCH onboarding-veje v1] Po vnosu podatkov o stranki se pot razcepi:
  // 'podatki' -> 'izbira' -> 'pregled' | 'uvoz'. Vstop s ?companyId=
  // (iz Profila podjetja) gre naravnost v 'uvoz', kot doslej.
  const [veja, setVeja] = useState<'podatki' | 'izbira' | 'pregled' | 'uvoz'>(
    obstojeciCompanyId
      ? (zeljenaVeja === 'pregled' ? 'pregled' : 'uvoz')
      : 'podatki');  // [PATCH pregled-vstop v1]

  // [PATCH onboarding-veje v1] Nova baza: izbrano podjetje se izprazni, da uporabnik
  // ne ostane v prejsnji bazi. Pri vstopu s ?companyId= OSTANE, ker se
  // sifranti uvazajo prav v to podjetje.
  const { setSelectedCompany } = useAuthStore();
  useEffect(() => {
    if (!obstojeciCompanyId) setSelectedCompany(null);
  }, [obstojeciCompanyId, setSelectedCompany]);

  const [nalaganje, setNalaganje] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);

  // [PATCH servis-profil v1] Podatki racunovodskega servisa (kartica A2).
  // Shranijo se na SERVIS (tenant), ne na podjetje. Potrebni so za
  // dokumente pregleda stranke; ce jih se ni, se obrazec odpre sam.
  const [servis, setServis] = useState<any | null>(null);
  const [servisUrejam, setServisUrejam] = useState(false);
  const [servisShranjujem, setServisShranjujem] = useState(false);
  const [servisNapaka, setServisNapaka] = useState<string | null>(null);
  // [PATCH servis-profil-baza v1] Nalaganje BREZ company_id je odstranjeno:
  // teklo je vzporedno z nalaganjem po podjetju in obe zanki sta pisali v
  // isto stanje. Kdo zmaga, je bilo odvisno od vrstnega reda odgovorov --
  // zato je kartica enkrat kazala servis podjetja, drugic servis uporabnika.
  // Profil se nalozi izkljucno v ucinku ob [companyId, veja].
  const servisPolje = (k: string, v: string) =>
    setServis((s: any) => ({ ...(s || {}), [k]: v }));
  const shraniServis = () => {
    if (!servis) return;
    setServisShranjujem(true);
    setServisNapaka(null);
    api.put('/servis/profil'
            + (companyId ? '?company_id=' + companyId : ''),  // [PATCH servis-profil-baza v1]
            servis)
      .then((r: any) => { setServis(r.data); setServisUrejam(false); })
      .catch((e: any) => setServisNapaka(
        e?.response?.data?.detail || 'Shranjevanje ni uspelo'))
      .finally(() => setServisShranjujem(false));
  };

  // Korak 1 — podatki o stranki
  const [naziv, setNaziv] = useState('');
  const [davcna, setDavcna] = useState('');       // vedno BREZ SI (SI = identifikacijska)
  const [vatNumber, setVatNumber] = useState(''); // ID za DDV (SI...) — interno, iz FURS
  const [naslov, setNaslov] = useState('');
  const [postnaSt, setPostnaSt] = useState('');
  const [posta, setPosta] = useState('');
  const [maticna, setMaticna] = useState('');
  const [skd, setSkd] = useState('');
  const [skdOpis, setSkdOpis] = useState(''); // opis dejavnosti -> skd_description
  const [ddvZavezanec, setDdvZavezanec] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(obstojeciCompanyId);
  // [PATCH opal-onboarding-E v1] Opal en klik: dialog potrditve baze + ukaz v ozadju
  const [opalDialog, setOpalDialog] = useState(false);
  const [vascoDialog, setVascoDialog] = useState(false);   // [PATCH vasco-sync-fe v1]
  const [opalUkaz, setOpalUkaz] = useState<any | null>(null);

  // [PATCH pregled-osebe v1] Osebe stranke: zakoniti zastopniki in dejanski
  // lastniki. Vezano na companyId, NE na obstojeciCompanyId -- pri novi
  // bazi je companyId znan sele po kliku Naprej.
  const [osebe, setOsebe] = useState<any[]>([]);

  // [PATCH pregled-dokumenti v1] izdelava, shramba in prenos dokumentov.
  // Endpoint je zascoten, zato <a href> ne zadosca -- datoteka se
  // prenese kot blob z avtoriziranim klicem (vzorec iz objav).
  const DOKUMENTI = [
    { vrsta: 'obrazec_pregled',
      naslov: 'Obrazec za pregled stranke in ocena tveganja' },
    { vrsta: 'pep',
      naslov: 'Vprašalnik za politično izpostavljene osebe' },
    { vrsta: 'gdpr_pogodba',
      naslov: 'Pogodba o obdelavi osebnih podatkov' },
  ];
  const [dokumenti, setDokumenti] = useState<any[]>([]);
  const [izdelujem, setIzdelujem] = useState<string | null>(null);
  const [dokNapaka, setDokNapaka] = useState<string | null>(null);
  const zadnjiDok = (vrsta: string) =>
    dokumenti.filter((x: any) => x.vrsta === vrsta)[0] || null;
  const prenesiDok = (d: any) => {
    if (!companyId || !d) return;
    api.get('/pregled/' + companyId + '/dokumenti/' + d.id,
            { responseType: 'blob' })
      .then((r: any) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = d.ime_datoteke;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setDokNapaka('Prenos ni uspel.'));
  };
  const izdelajDok = (vrsta: string) => {
    if (!companyId) return;
    setIzdelujem(vrsta);
    setDokNapaka(null);
    api.post('/pregled/' + companyId + '/izdelaj/' + vrsta, {})
      .then((r: any) => {
        const seznam = r.data?.dokumenti || [];
        setDokumenti(seznam);
        const nov = seznam.filter((x: any) => x.vrsta === vrsta)[0];
        if (nov) prenesiDok(nov);
      })
      .catch((e: any) => setDokNapaka(
        e?.response?.data?.detail || 'Izdelava ni uspela.'))
      .finally(() => setIzdelujem(null));
  };

  // [PATCH pregled-obrazec v1] Obrazec pregleda. Isti klic kot osebe.
  // Stiri vprasanja so DA/NE brez tretjega stanja: null iz baze se ob
  // nalaganju prevede v false.
  const VPRASANJA = ['pep', 'tvegana_drzava', 'sum_pd_ft', 'obvestilo_urad'];
  const [pregled, setPregled] = useState<any | null>(null);

  // [PATCH pregled-zlozljivo v1] Redka bloka (transakcija, poglobljeni pregled) sta
  // privzeto zaprta. null pomeni 'uporabnik se ni odlocil' -- takrat
  // odloca vsebina: blok s podatki je odprt, da se izpolnjeno ne skrije.
  const [trOdprt, setTrOdprt] = useState<boolean | null>(null);
  const [pgOdprt, setPgOdprt] = useState<boolean | null>(null);
  const jePolno = (o: any): boolean =>
    Object.keys(o || {}).some((k) => {
      const v = (o || {})[k];
      if (Array.isArray(v)) return v.some((x: any) => !!x);
      return !!v;
    });
  const trVidno = () =>
    (trOdprt === null ? jePolno(pregled?.transakcija) : trOdprt);
  const pgVidno = () =>
    (pgOdprt === null ? jePolno(pregled?.poglobljeni) : pgOdprt);
  const [pregledShranjujem, setPregledShranjujem] = useState(false);
  const [pregledShranjen, setPregledShranjen] = useState(false);
  const [pregledNapaka, setPregledNapaka] = useState<string | null>(null);
  const normPregled = (p: any) => {
    if (!p) return null;
    const n: any = { ...p };
    VPRASANJA.forEach((k) => { n[k] = !!n[k]; });
    n.transakcija = n.transakcija || {};
    n.poglobljeni = n.poglobljeni || {};
    n.poglobljeni.razlogi = n.poglobljeni.razlogi || [];
    n.poglobljeni.premozenjsko_stanje =
      n.poglobljeni.premozenjsko_stanje || ['', '', '', '', ''];
    return n;
  };
  const pregledPolje = (k: string, v: any) => {
    setPregledShranjen(false);
    setPregled((s: any) => ({ ...(s || {}), [k]: v }));
  };
  const trPolje = (k: string, v: any) => {
    setPregledShranjen(false);
    setPregled((s: any) => ({
      ...(s || {}),
      transakcija: { ...((s || {}).transakcija || {}), [k]: v },
    }));
  };
  const pgPolje = (k: string, v: any) => {
    setPregledShranjen(false);
    setPregled((s: any) => ({
      ...(s || {}),
      poglobljeni: { ...((s || {}).poglobljeni || {}), [k]: v },
    }));
  };
  const pgStanje = (i: number, v: string) => {
    setPregledShranjen(false);
    setPregled((s: any) => {
      const pg: any = { ...((s || {}).poglobljeni || {}) };
      const vrstice = [...(pg.premozenjsko_stanje || ['', '', '', '', ''])];
      vrstice[i] = v;
      pg.premozenjsko_stanje = vrstice;
      return { ...(s || {}), poglobljeni: pg };
    });
  };
  const pgRazlog = (k: string, vklop: boolean) => {
    setPregledShranjen(false);
    setPregled((s: any) => {
      const pg: any = { ...((s || {}).poglobljeni || {}) };
      const r: any[] = [...(pg.razlogi || [])].filter((x) => x !== k);
      if (vklop) r.push(k);
      pg.razlogi = r;
      return { ...(s || {}), poglobljeni: pg };
    });
  };
  const shraniPregled = () => {
    if (!companyId || !pregled) return;
    const telo: any = { ...pregled };
    delete telo.id;
    ['datum_sklenitve', 'datum_pregleda'].forEach((k) => {
      if (!telo[k]) telo[k] = null;
    });
    setPregledShranjujem(true);
    setPregledNapaka(null);
    api.put('/pregled/' + companyId, telo)
      .then((r: any) => {
        setPregled(normPregled(r.data?.pregled || null));
        setPregledShranjen(true);
      })
      .catch((e: any) => setPregledNapaka(
        e?.response?.data?.detail || 'Shranjevanje ni uspelo'))
      .finally(() => setPregledShranjujem(false));
  };
  const [osebaVnos, setOsebaVnos] = useState<any | null>(null);
  const [osebaShranjujem, setOsebaShranjujem] = useState(false);
  const [osebaNapaka, setOsebaNapaka] = useState<string | null>(null);
  useEffect(() => {
    if (!companyId || veja !== 'pregled') return;
    api.get('/pregled/' + companyId)
      .then((r: any) => {
        setOsebe(r.data?.osebe || []);
        setPregled(normPregled(r.data?.pregled || null));  // [PATCH pregled-obrazec v1]
      })
      .catch(() => { setOsebe([]); setPregled(null); });
    api.get('/pregled/' + companyId + '/dokumenti')  // [PATCH pregled-dokumenti v1]
      .then((r: any) => setDokumenti(r.data?.dokumenti || []))
      .catch(() => setDokumenti([]));
    // [PATCH servis-profil-baza v1] profil servisa, ki vodi TO bazo. Pri superadminu
    // v tuji bazi to ni isti servis kot njegov lastni.
    api.get('/servis/profil?company_id=' + companyId)
      .then((r: any) => {
        setServis(r.data);
        if ((r.data?.manjka || []).length > 0) setServisUrejam(true);
      })
      .catch(() => {});
  }, [companyId, veja]);
  const zacniOsebo = (vrsta: string) => {
    setOsebaNapaka(null);
    setOsebaVnos({ id: null, vrsta, je_podpisnik: false, ime_priimek: '' });
  };
  const urediOsebo = (o: any) => {
    setOsebaNapaka(null);
    setOsebaVnos({ ...o });
  };
  const osebaPolje = (k: string, v: any) =>
    setOsebaVnos((s: any) => ({ ...(s || {}), [k]: v }));
  const shraniOsebo = () => {
    if (!companyId || !osebaVnos) return;
    if (!(osebaVnos.ime_priimek || '').trim()) {
      setOsebaNapaka('Ime in priimek sta obvezna.');
      return;
    }
    // prazen niz ni veljaven date -- pydantic bi vnos zavrnil
    const telo: any = { ...osebaVnos };
    ['datum_rojstva', 'dok_velja_do'].forEach((k) => {
      if (!telo[k]) telo[k] = null;
    });
    setOsebaShranjujem(true);
    setOsebaNapaka(null);
    const pot = '/pregled/' + companyId + '/osebe';
    const klic = osebaVnos.id
      ? api.put(pot + '/' + osebaVnos.id, telo)
      : api.post(pot, telo);
    klic
      .then((r: any) => { setOsebe(r.data?.osebe || []); setOsebaVnos(null); })
      .catch((e: any) => setOsebaNapaka(
        e?.response?.data?.detail || 'Shranjevanje ni uspelo'))
      .finally(() => setOsebaShranjujem(false));
  };
  const izbrisiOsebo = (o: any) => {
    if (!companyId) return;
    if (!window.confirm('Izbrišem osebo ' + o.ime_priimek + '?')) return;
    api.delete('/pregled/' + companyId + '/osebe/' + o.id)
      .then((r: any) => setOsebe(r.data?.osebe || []))
      .catch(() => {});
  };
  const [ibaniAjpes, setIbaniAjpes] = useState<string[]>([]);  // [PATCH rtr-onboarding v1] odprti TRR iz AJPES RTR

  // [PATCH pregled-vstop v1] Polja koraka 1 se napolnijo samo ob vnosu. Za
  // obstojece podjetje jih je treba prebrati iz baze, sicer je izpis
  // "Podatki o stranki" v veji pregleda prazen.
  useEffect(() => {
    if (!obstojeciCompanyId || zeljenaVeja !== 'pregled') return;
    api.get('/companies/' + obstojeciCompanyId)
      .then((r: any) => {
        const c = r.data || {};
        setNaziv(c.name || '');
        setDavcna((c.tax_number || '').replace(/^SI/i, ''));
        setNaslov(c.address || '');
        setPostnaSt(c.postal_code || '');
        setPosta(c.city || '');
        setMaticna(c.registration_number || '');
        setSkd(c.skd_code || '');
        setSkdOpis(c.skd_description || '');
      })
      .catch(() => {});
  }, [obstojeciCompanyId, zeljenaVeja]);

  // Poišči (FURS/AJPES lookup)
  const [preverjam, setPreverjam] = useState(false);
  const [lookup, setLookup] = useState<{ tip: 'ok' | 'napaka'; besedilo: string } | null>(null);

  // Korak 2 — program
  const [program, setProgram] = useState<Program>('vasco');

  // Koraka 3/4 — datoteke
  const [sloti, setSloti] = useState<DatotekaSlot[]>(
    VASCO_SLOTI.map((s) => ({ ...s, file: null }))
  );
  const [kopiranSql, setKopiranSql] = useState<string | null>(null);

  // Korak 5 — rezultat
  const [rezultat, setRezultat] = useState<any>(null);

  const izberiProgram = (p: Program) => {
    setProgram(p);
    setSloti(SLOTI_ZA_PROGRAM[p].map((s) => ({ ...s, file: null })));
  };

  // Poišči po davčni — isti endpoint kot maska partnerja (PartnerFursForm)
  const poisci = async () => {
    const vnos = davcna.trim().replace(/^SI/i, '');
    if (!vnos) {
      setLookup({ tip: 'napaka', besedilo: 'Vpiši davčno številko.' });
      return;
    }
    setPreverjam(true);
    setLookup(null);
    try {
      const r = await api.get(`/partner-lookup?davcna=${encodeURIComponent(vnos)}`);
      const d = r.data;
      if (!d || (!d.naziv && !d.ddv_id && !d.maticna)) {
        setLookup({ tip: 'napaka', besedilo: 'Ni podatkov za to davčno številko.' });
        return;
      }
      setDavcna(vnos);
      if (d.naziv) setNaziv(String(d.naziv));
      // razčleni naslov "Ulica 1, 1000 Ljubljana" -> naslov / poštna / pošta
      const m = d.naslov ? String(d.naslov).match(/^(.+),\s*(\d{4,5})\s+(.+)$/) : null;
      if (m) {
        setNaslov(m[1].replace(/\s+/g, ' ').trim());
        setPostnaSt(m[2]);
        setPosta(m[3].trim());
      } else if (d.naslov) {
        setNaslov(String(d.naslov));
      }
      if (d.maticna) setMaticna(String(d.maticna));
      if (d.skd) setSkd(String(d.skd));
      if (d.skd_naziv) setSkdOpis(String(d.skd_naziv));
      if (d.ddv_id) setVatNumber(String(d.ddv_id));
      if (d.vir !== 'VIES') setDdvZavezanec(!!d.zavezanec_ddv);
      const vir = d.vir || 'FURS';
      setLookup({ tip: 'ok', besedilo: `Najdeno (${vir}): ${d.naziv || d.ddv_id || vnos}` });
      /* [PATCH rtr-onboarding v1] poleg FURS povleci se ODPRTE TRR iz AJPES RTR */
      try {
        const rr: any = await api.get('/sifranti/rtr-iban?davcna=' + encodeURIComponent(vnos));
        const vsi: any[] = Array.isArray(rr.data?.racuni) ? rr.data.racuni : [];
        setIbaniAjpes(vsi.filter((x: any) => x && x.iban && x.odprt).map((x: any) => String(x.iban)));
      } catch { setIbaniAjpes([]); }
    } catch (e: any) {
      setLookup({ tip: 'napaka', besedilo: e?.response?.data?.detail || 'Napaka pri iskanju.' });
    } finally {
      setPreverjam(false);
    }
  };

  const kreirajBazo = async () => {
    setNapaka(null);
    if (!naziv.trim()) {
      setNapaka('Naziv je obvezen.');
      return;
    }
    setNalaganje(true);
    try {
      const res = await api.post('/companies/', {
        name: naziv.trim(),
        tax_number: davcna.trim().replace(/^SI/i, '') || null,
        vat_number: vatNumber.trim() || null,
        registration_number: maticna.trim() || null,
        address: naslov.trim() || null,
        postal_code: postnaSt.trim() || null,
        city: posta.trim() || null,
        skd_code: skd.trim() || null,
        skd_description: skdOpis.trim() || null,
        vat_status: ddvZavezanec ? 'Zavezanec' : 'Ni zavezanec',
        vat_regime: ddvZavezanec ? 'Splošni' : null, // tiho privzeto (uporabnik uredi na profilu)
      });
      setCompanyId(res.data.id);
      /* [PATCH rtr-onboarding v1] shrani TRR iz AJPES na novo kreirano podjetje.
         Ce spodleti, onboardinga NE prekinemo - racune je mogoce dodati na
         Profilu podjetja z gumbom "Preveri AJPES". */
      if (ibaniAjpes.length) {
        try {
          await api.put('/companies/' + res.data.id + '/ibani', { ibani: ibaniAjpes });
        } catch { /* tiho */ }
      }
      setVeja('izbira');  // [PATCH onboarding-veje v1] podatki -> izbira, ne naravnost uvoz
    } catch (e: any) {
      setNapaka(e?.response?.data?.detail || 'Napaka pri kreiranju baze.');
    } finally {
      setNalaganje(false);
    }
  };

  const izberiDatoteko = (kljuc: string, file: File | null) => {
    setSloti((prev) => prev.map((s) => (s.kljuc === kljuc ? { ...s, file } : s)));
  };

  const kopirajSql = async (kljuc: string, sql: string) => {
    try {
      await navigator.clipboard.writeText(sql);
    } catch {
      /* clipboard ni na voljo (npr. ne-https) — tiho */
    }
    setKopiranSql(kljuc);
    setTimeout(() => setKopiranSql((k) => (k === kljuc ? null : k)), 1500);
  };

  const vsajEna = sloti.some((s) => s.file !== null);

  const zazeniUvoz = async () => {
    setNapaka(null);
    if (!companyId) {
      setNapaka('Manjka ID baze.');
      return;
    }
    if (!vsajEna) {
      setNapaka('Naloži vsaj eno datoteko.');
      return;
    }
    setNalaganje(true);
    try {
      const form = new FormData();
      form.append('program', program);
      sloti.forEach((s) => {
        if (s.file) form.append(s.kljuc, s.file);
      });
      const res = await api.post(`/onboarding/${companyId}/uvoz`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRezultat(res.data);
      setKorak(5);
    } catch (e: any) {
      setNapaka(e?.response?.data?.detail || 'Napaka pri uvozu.');
    } finally {
      setNalaganje(false);
    }
  };

  // skupna vrstica za slot (koraka 3 in 4)
  const slotVrstica = (s: DatotekaSlot) => (
    <div key={s.kljuc} className="flex items-center justify-between p-3 border rounded-lg dark:border-gray-700">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs ${
            s.file ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
          }`}
        >
          {s.file ? '✓' : ''}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{s.label}</div>
          <div className="text-xs text-gray-500 truncate">{s.file ? s.file.name : s.opis}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {program === 'opal' && s.sql && (
          <button
            type="button"
            onClick={() => kopirajSql(s.kljuc, s.sql!)}
            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1 dark:text-gray-200"
            title="Kopiraj SQL za Opal"
          >
            {kopiranSql === s.kljuc ? (
              <span>✓ Kopirano</span>
            ) : (
              <>
                <Copy className="w-3 h-3" /> Kopiraj SQL
              </>
            )}
          </button>
        )}
        <label className="text-sm text-blue-600 cursor-pointer hover:underline">
          {s.file ? 'Zamenjaj' : 'Naloži'}
          <input
            type="file"
            accept=".xls,.xlsx"
            className="hidden"
            onChange={(e) => izberiDatoteko(s.kljuc, e.target.files?.[0] || null)}
          />
        </label>
      </div>
    </div>
  );

  const opalNamig = program === 'opal' && (
    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
      Klikni <span className="font-medium">Kopiraj SQL</span>, prilepi v Opal (SQL poizvedbe → Hitra SQL
      poizvedba), izvozi rezultat v Excel in ga naloži. Poizvedbe so brez <code>;</code> in <code>--</code>.
    </div>
  );

  // [PATCH onboarding-veje v1] Kartica servisa se je preselila iz koraka 1
  // v vejo "Pregled stranke". Vsebina je prenesena NESPREMENJENA.
  // Izvor: [PATCH servis-profil v1] kartica A2.
  const kartaServisa = (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Podatki računovodskega servisa</h3>
              {servis && !servisUrejam && (
                <button type="button" onClick={() => setServisUrejam(true)}
                  className="text-xs text-blue-600 hover:underline">Uredi</button>
              )}
            </div>
            {!servis ? (
              <p className="text-xs text-gray-500">Nalagam…</p>
            ) : servisUrejam ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Vpiše se enkrat za celoten servis. Uporabi se v glavi obrazcev in v pogodbi.
                </p>
              <div>
                <label className="block text-xs font-medium mb-1">Naziv za dokumente</label>
                <input value={servis.naziv_dokumenti || ''}
                  onChange={(e) => servisPolje('naziv_dokumenti', e.target.value)}
                  className={`${inputCls} text-sm`} placeholder="prazno = uporabi se ime servisa" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Naslov (ulica in hišna št.)</label>
                <input value={servis.address || ''}
                  onChange={(e) => servisPolje('address', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
                <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Poštna št.</label>
                <input value={servis.postal_code || ''}
                  onChange={(e) => servisPolje('postal_code', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium mb-1">Pošta</label>
                    <input value={servis.city || ''}
                      onChange={(e) => servisPolje('city', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Davčna številka</label>
                <input value={servis.tax_number || ''}
                  onChange={(e) => servisPolje('tax_number', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Matična številka</label>
                <input value={servis.registration_number || ''}
                  onChange={(e) => servisPolje('registration_number', e.target.value)}
                  className={`${inputCls} text-sm`} placeholder="neobvezno" />
              </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Zastopnik</label>
                <input value={servis.zastopnik_ime || ''}
                  onChange={(e) => servisPolje('zastopnik_ime', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Funkcija zastopnika</label>
                <input value={servis.zastopnik_funkcija || ''}
                  onChange={(e) => servisPolje('zastopnik_funkcija', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Kraj podpisa (npr. Žiri)</label>
                <input value={servis.kraj_podpisa_mestnik || ''}
                  onChange={(e) => servisPolje('kraj_podpisa_mestnik', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Pristojno sodišče (npr. Kranj)</label>
                <input value={servis.sodisce_mestnik || ''}
                  onChange={(e) => servisPolje('sodisce_mestnik', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
                </div>
                <p className="text-[10px] text-gray-500 -mt-1">
                  V dokument gre »Kraj: …« in »sodišče v kraju …«.
                </p>
                <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Pregled stranke opravi</label>
                <input value={servis.pregled_oseba_ime || ''}
                  onChange={(e) => servisPolje('pregled_oseba_ime', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Funkcija te osebe</label>
                <input value={servis.pregled_oseba_funkcija || ''}
                  onChange={(e) => servisPolje('pregled_oseba_funkcija', e.target.value)}
                  className={`${inputCls} text-sm`} />
              </div>
                </div>
                {servisNapaka && (
                  <div className="text-xs text-red-600">{servisNapaka}</div>
                )}
                <button type="button" onClick={shraniServis}
                  disabled={servisShranjujem}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg">
                  {servisShranjujem ? 'Shranjujem…' : 'Shrani'}
                </button>
              </div>
            ) : (
              <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                <div className="font-medium text-gray-900 dark:text-white">
                  {servis.naziv_dokumenti || servis.name}
                </div>
                <div>
                  {[servis.address, [servis.postal_code, servis.city]
                    .filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}
                </div>
                <div>
                  Davčna: {servis.tax_number || '—'} · Zastopa: {servis.zastopnik_ime || '—'}
                  {servis.zastopnik_funkcija ? ', ' + servis.zastopnik_funkcija : ''}
                </div>
                <div>
                  Podpis v: {servis.kraj_podpisa_mestnik || '—'} · Sodišče v: {servis.sodisce_mestnik || '—'}
                </div>
                <div>
                  Pregled opravi: {servis.pregled_oseba_ime || '—'}
                  {servis.pregled_oseba_funkcija ? ', ' + servis.pregled_oseba_funkcija : ''}
                </div>
                {(servis.manjka || []).length > 0 && (
                  <div className="text-amber-600 dark:text-amber-400 pt-1">
                    Za izdelavo dokumentov manjka: {(servis.manjka || []).join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* [PATCH onboarding-veje v1] naslov po veji */}
      <h1 className="text-2xl font-bold mb-6">
        {veja === 'pregled'
          ? 'Pregled stranke'
          : veja === 'uvoz'
          ? 'Nova baza — uvoz podatkov'
          : 'Novo podjetje'}
      </h1>

      {/* Koraki indikator — samo v veji uvoza */}
      {veja === 'uvoz' && (
      <div className="flex items-center gap-2 mb-8">
        {KORAKI.map((k) => (
          <div key={k} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                korak >= k ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {k}
            </div>
            {k < KORAKI.length && <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700" />}
          </div>
        ))}
      </div>
      )}

      {napaka && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{napaka}</div>
      )}

      {/* KORAK 1 — podatki o stranki */}
      {/* [PATCH onboarding-veje v1] korak 1 -> veja 'podatki' */}
      {veja === 'podatki' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">1. Podatki o stranki</h2>

          {/* davčna + Poišči */}
          <div>
            <label className="block text-sm font-medium mb-1">Davčna številka (brez SI)</label>
            <div className="flex gap-2">
              <input
                value={davcna}
                onChange={(e) => setDavcna(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    poisci();
                  }
                }}
                className={inputCls}
              />
              <button
                type="button"
                onClick={poisci}
                disabled={preverjam}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg whitespace-nowrap flex items-center gap-1"
              >
                <Search className="w-4 h-4" />
                {preverjam ? 'Iščem…' : 'Poišči'}
              </button>
            </div>
            {lookup && (
              <div
                className={`mt-1 text-xs ${
                  lookup.tip === 'ok' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                }`}
              >
                {lookup.besedilo}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Naziv *</label>
            <input value={naziv} onChange={(e) => setNaziv(e.target.value)} className={inputCls} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Naslov</label>
            <input value={naslov} onChange={(e) => setNaslov(e.target.value)} className={inputCls} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Poštna št.</label>
              <input value={postnaSt} onChange={(e) => setPostnaSt(e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Pošta</label>
              <input value={posta} onChange={(e) => setPosta(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Matična številka</label>
              <input value={maticna} onChange={(e) => setMaticna(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Šifra SKD</label>
              <input value={skd} onChange={(e) => setSkd(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">TRR (IBAN)</label>
            {/* [PATCH rtr-onboarding v1] prikaz odprtih racunov iz AJPES RTR */}
            {ibaniAjpes.length > 0 ? (
              <div className="rounded-xl border border-green-300 dark:border-green-800 bg-green-50/60 dark:bg-green-950/20 p-2 space-y-1">
                {ibaniAjpes.map((i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-green-600 dark:text-green-400">✓</span>
                    <span className="font-mono text-gray-700 dark:text-gray-200">{i}</span>
                  </div>
                ))}
                <p className="text-[10px] text-gray-500 pt-1">
                  Iz AJPES registra (RTR) — shranjeni bodo ob kreiranju baze.</p>
              </div>
            ) : (
              <input
                value=""
                disabled
                className={`${inputCls} opacity-60 cursor-not-allowed`}
                placeholder={davcna.trim() ? 'ni odprtih računov v AJPES registru' : 'samodejno iz AJPES — vpiši davčno in klikni Poišči'}
              />
            )}
          </div>

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={ddvZavezanec} onChange={(e) => setDdvZavezanec(e.target.checked)} />
            <span className="text-sm">DDV zavezanec</span>
          </label>

          <button
            onClick={kreirajBazo}
            disabled={nalaganje}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl disabled:opacity-50"
          >
            {nalaganje ? 'Kreiram…' : 'Naprej'}
          </button>
        </div>
      )}

      {/* [PATCH onboarding-veje v1] IZBIRA — kaj zelis narediti */}
      {veja === 'izbira' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Kaj želiš narediti?</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Podjetje je ustvarjeno. Oboje lahko narešiš ločeno in kadar koli kasneje.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setVeja('pregled')}
              className="p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-left hover:border-blue-400 dark:text-white"
            >
              <div className="font-semibold">Pregled stranke</div>
              <div className="text-xs text-gray-500 mt-1">
                Obrazec za pregled, vprašalnik PEP, pogodba o obdelavi osebnih podatkov
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setVeja('uvoz'); setKorak(2); }}
              className="p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-left hover:border-blue-400 dark:text-white"
            >
              <div className="font-semibold">Uvoz šifrantov in knjižb</div>
              <div className="text-xs text-gray-500 mt-1">
                Prenos podatkov iz Vasca ali Opala
              </div>
            </button>
          </div>
        </div>
      )}

      {/* [PATCH onboarding-veje v1] PREGLED STRANKE — dokumenti pridejo v fazah 2-3 */}
      {veja === 'pregled' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold mb-2">Podatki o stranki</h3>
            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
              <div className="font-medium text-gray-900 dark:text-white">
                {naziv || '—'}
              </div>
              <div>
                {[naslov, [postnaSt, posta].filter(Boolean).join(' ')]
                  .filter(Boolean).join(', ') || '—'}
              </div>
              <div>
                Davčna: {davcna || '—'} · Matična: {maticna || '—'}
              </div>
              <div>
                Dejavnost: {skd ? skd + (skdOpis ? ' (' + skdOpis + ')' : '') : '—'}
              </div>
            </div>
          </div>

          {kartaServisa}

          {/* [PATCH pregled-osebe v1] zastopniki in dejanski lastniki */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Osebe</h3>
              {!osebaVnos && companyId && (
                <div className="flex gap-3">
                  <button type="button" onClick={() => zacniOsebo('zastopnik')}
                    className="text-xs text-blue-600 hover:underline">+ zastopnik</button>
                  <button type="button" onClick={() => zacniOsebo('lastnik')}
                    className="text-xs text-blue-600 hover:underline">+ dejanski lastnik</button>
                </div>
              )}
            </div>

            {osebe.length === 0 && !osebaVnos && (
              <p className="text-xs text-gray-500">
                Ni vpisanih oseb. Za obrazec pregleda je potreben vsaj
                zakoniti zastopnik.
              </p>
            )}

            {osebe.map((o: any) => (
              <div
                key={o.id}
                className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 dark:border-gray-800 last:border-0 py-1.5"
              >
                <div className="min-w-0">
                  <div className="text-gray-900 dark:text-white truncate">
                    {o.ime_priimek}
                    {o.funkcija ? ', ' + o.funkcija : ''}
                  </div>
                  <div className="text-xs text-gray-500">
                    {o.vrsta === 'zastopnik'
                      ? 'zakoniti zastopnik'
                      : 'dejanski lastnik'}
                    {o.delez_opis ? ' · ' + o.delez_opis : ''}
                    {o.je_podpisnik ? ' · podpisnik PEP' : ''}
                  </div>
                </div>
                <div className="flex gap-3 shrink-0">
                  <button type="button" onClick={() => urediOsebo(o)}
                    className="text-xs text-blue-600 hover:underline">Uredi</button>
                  <button type="button" onClick={() => izbrisiOsebo(o)}
                    className="text-xs text-red-600 hover:underline">Izbriši</button>
                </div>
              </div>
            ))}

            {osebaVnos && (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium">
                  {osebaVnos.vrsta === 'zastopnik'
                    ? 'Zakoniti zastopnik'
                    : 'Dejanski lastnik (nad 25 %)'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Ime in priimek *</label>
                    <input value={osebaVnos.ime_priimek || ''}
                      onChange={(e) => osebaPolje('ime_priimek', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Funkcija</label>
                    <input value={osebaVnos.funkcija || ''}
                      onChange={(e) => osebaPolje('funkcija', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Naslov stalnega prebivališča</label>
                    <input value={osebaVnos.naslov_stalni || ''}
                      onChange={(e) => osebaPolje('naslov_stalni', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Naslov začasnega prebivališča</label>
                    <input value={osebaVnos.naslov_zacasni || ''}
                      onChange={(e) => osebaPolje('naslov_zacasni', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Datum rojstva</label>
                    <input type="date" value={osebaVnos.datum_rojstva || ''}
                      onChange={(e) => osebaPolje('datum_rojstva', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Kraj rojstva</label>
                    <input value={osebaVnos.kraj_rojstva || ''}
                      onChange={(e) => osebaPolje('kraj_rojstva', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Davčna številka ali EMŠO</label>
                    <input value={osebaVnos.davcna_emso || ''}
                      onChange={(e) => osebaPolje('davcna_emso', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Državljanstvo</label>
                    <input value={osebaVnos.drzavljanstvo || ''}
                      onChange={(e) => osebaPolje('drzavljanstvo', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Vrsta osebnega dokumenta</label>
                    <input value={osebaVnos.dok_vrsta || ''}
                      onChange={(e) => osebaPolje('dok_vrsta', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Številka dokumenta</label>
                    <input value={osebaVnos.dok_stevilka || ''}
                      onChange={(e) => osebaPolje('dok_stevilka', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Naziv izdajatelja</label>
                    <input value={osebaVnos.dok_izdajatelj || ''}
                      onChange={(e) => osebaPolje('dok_izdajatelj', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Rok veljavnosti</label>
                    <input type="date" value={osebaVnos.dok_velja_do || ''}
                      onChange={(e) => osebaPolje('dok_velja_do', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                </div>
                {osebaVnos.vrsta === 'lastnik' && (
                  <div>
                    <label className="block text-xs font-medium mb-1">Višina deleža ali drug način nadzora</label>
                    <input value={osebaVnos.delez_opis || ''}
                      onChange={(e) => osebaPolje('delez_opis', e.target.value)}
                      className={`${inputCls} text-sm`} />
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!osebaVnos.je_podpisnik}
                    onChange={(e) => osebaPolje('je_podpisnik', e.target.checked)} />
                  <span>Podpisnik vprašalnika PEP (samo ena oseba)</span>
                </label>
                {osebaNapaka && (
                  <div className="text-xs text-red-600">{osebaNapaka}</div>
                )}
                <div className="flex gap-2">
                  <button type="button" onClick={shraniOsebo}
                    disabled={osebaShranjujem}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg">
                    {osebaShranjujem ? 'Shranjujem…' : 'Shrani osebo'}
                  </button>
                  <button type="button" onClick={() => setOsebaVnos(null)}
                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:text-white">
                    Prekliči
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* [PATCH pregled-obrazec v1] obrazec pregleda stranke */}
          {pregled && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Obrazec pregleda</h3>
              {pregledShranjen && (
                <span className="text-xs text-green-600 dark:text-green-400">Shranjeno</span>
              )}
            </div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Poslovno razmerje</p>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Namen in narava poslovnega razmerja</label>
                  <input value={pregled.namen_razmerja || ''}
                    onChange={(e) => pregledPolje('namen_razmerja', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Datum sklenitve poslovnega razmerja</label>
                  <input type="date" value={pregled.datum_sklenitve || ''}
                    onChange={(e) => pregledPolje('datum_sklenitve', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div className="text-xs text-gray-500">
                  Dejavnost stranke: {pregled.dejavnost_opis || '—'}
                </div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Vprašanja</p>
                <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <span className="text-xs text-gray-700 dark:text-gray-200">Ali je stranka politično izpostavljena oseba?</span>
                  <div className="flex gap-3 shrink-0">
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.pep === true}
                        onChange={() => pregledPolje('pep', true)} />
                      <span>DA</span>
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.pep === false}
                        onChange={() => pregledPolje('pep', false)} />
                      <span>NE</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <span className="text-xs text-gray-700 dark:text-gray-200">Ali je stranka ali transakcija povezana z visoko tvegano tretjo državo?</span>
                  <div className="flex gap-3 shrink-0">
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.tvegana_drzava === true}
                        onChange={() => pregledPolje('tvegana_drzava', true)} />
                      <span>DA</span>
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.tvegana_drzava === false}
                        onChange={() => pregledPolje('tvegana_drzava', false)} />
                      <span>NE</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <span className="text-xs text-gray-700 dark:text-gray-200">Ali je prišlo do suma pranja denarja ali financiranja terorizma?</span>
                  <div className="flex gap-3 shrink-0">
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.sum_pd_ft === true}
                        onChange={() => pregledPolje('sum_pd_ft', true)} />
                      <span>DA</span>
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.sum_pd_ft === false}
                        onChange={() => pregledPolje('sum_pd_ft', false)} />
                      <span>NE</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <span className="text-xs text-gray-700 dark:text-gray-200">Ali je bilo Uradu posredovano obvestilo o stranki oziroma transakciji?</span>
                  <div className="flex gap-3 shrink-0">
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.obvestilo_urad === true}
                        onChange={() => pregledPolje('obvestilo_urad', true)} />
                      <span>DA</span>
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="radio" checked={pregled.obvestilo_urad === false}
                        onChange={() => pregledPolje('obvestilo_urad', false)} />
                      <span>NE</span>
                    </label>
                  </div>
                </div>
                <button type="button" onClick={() => setTrOdprt(!trVidno())}
                  className="w-full flex items-center justify-between gap-2 pt-2 text-left">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Transakcija 15.000 EUR ali več
                  </span>
                  <span className="text-xs text-blue-600 shrink-0">
                    {jePolno(pregled.transakcija) ? 'izpolnjeno · ' : ''}{trVidno() ? 'skrij' : 'prikaži'}
                  </span>
                </button>
                {trVidno() && (
                <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Datum in čas izvedbe</label>
                  <input value={pregled.transakcija.datum_cas || ''}
                    onChange={(e) => trPolje('datum_cas', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Znesek transakcije</label>
                  <input value={pregled.transakcija.znesek || ''}
                    onChange={(e) => trPolje('znesek', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Valuta</label>
                  <input value={pregled.transakcija.valuta || ''}
                    onChange={(e) => trPolje('valuta', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Namen transakcije</label>
                  <input value={pregled.transakcija.namen || ''}
                    onChange={(e) => trPolje('namen', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Prejemnik — osebno ime ali firma</label>
                  <input value={pregled.transakcija.prejemnik_ime || ''}
                    onChange={(e) => trPolje('prejemnik_ime', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Prejemnik — prebivališče ali sedež</label>
                  <input value={pregled.transakcija.prejemnik_naslov || ''}
                    onChange={(e) => trPolje('prejemnik_naslov', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Država, v katero je transakcija izvršena</label>
                  <input value={pregled.transakcija.drzava || ''}
                    onChange={(e) => trPolje('drzava', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Način izvedbe</label>
                  <input value={pregled.transakcija.nacin || ''}
                    onChange={(e) => trPolje('nacin', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                </div>
                )}
                <button type="button" onClick={() => setPgOdprt(!pgVidno())}
                  className="w-full flex items-center justify-between gap-2 pt-2 text-left">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Poglobljeni pregled
                  </span>
                  <span className="text-xs text-blue-600 shrink-0">
                    {jePolno(pregled.poglobljeni) ? 'izpolnjeno · ' : ''}{pgVidno() ? 'skrij' : 'prikaži'}
                  </span>
                </button>
                {pgVidno() && (
                <div className="space-y-3">
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox"
                      checked={(pregled.poglobljeni.razlogi || []).indexOf('tvegana_drzava') >= 0}
                      onChange={(e) => pgRazlog('tvegana_drzava', e.target.checked)} />
                    <span>povezana s tvegano državo</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox"
                      checked={(pregled.poglobljeni.razlogi || []).indexOf('pep') >= 0}
                      onChange={(e) => pgRazlog('pep', e.target.checked)} />
                    <span>politično izpostavljena oseba</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox"
                      checked={(pregled.poglobljeni.razlogi || []).indexOf('poveceno_tveganje') >= 0}
                      onChange={(e) => pgRazlog('poveceno_tveganje', e.target.checked)} />
                    <span>zaznano povečano tveganje</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox"
                      checked={(pregled.poglobljeni.razlogi || []).indexOf('sum') >= 0}
                      onChange={(e) => pgRazlog('sum', e.target.checked)} />
                    <span>sum za pranje denarja ali financiranje terorizma</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox"
                      checked={(pregled.poglobljeni.razlogi || []).indexOf('drugo') >= 0}
                      onChange={(e) => pgRazlog('drugo', e.target.checked)} />
                    <span>drugo</span>
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Drugo — opis</label>
                  <input value={pregled.poglobljeni.razlog_drugo || ''}
                    onChange={(e) => pgPolje('razlog_drugo', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Znesek</label>
                  <input value={pregled.poglobljeni.znesek || ''}
                    onChange={(e) => pgPolje('znesek', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Valuta</label>
                  <input value={pregled.poglobljeni.valuta || ''}
                    onChange={(e) => pgPolje('valuta', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Vrsta sredstev in premoženja</label>
                  <input value={pregled.poglobljeni.vrsta_sredstev || ''}
                    onChange={(e) => pgPolje('vrsta_sredstev', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Vrednost sredstev in premoženja</label>
                  <input value={pregled.poglobljeni.vrednost || ''}
                    onChange={(e) => pgPolje('vrednost', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Datum ali obdobje opravljanja transakcij</label>
                  <input value={pregled.poglobljeni.datum_obdobje || ''}
                    onChange={(e) => pgPolje('datum_obdobje', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Razlogi za sum pranja denarja ali financiranja terorizma</label>
                  <textarea rows={2} value={pregled.poglobljeni.razlogi_sum || ''}
                    onChange={(e) => pgPolje('razlogi_sum', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <p className="text-xs font-medium">Premoženjsko stanje ter sredstva in premoženje</p>
                <div>
                  <label className="block text-xs font-medium mb-1">1.</label>
                  <input value={(pregled.poglobljeni.premozenjsko_stanje || [])[0] || ''}
                    onChange={(e) => pgStanje(0, e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">2.</label>
                  <input value={(pregled.poglobljeni.premozenjsko_stanje || [])[1] || ''}
                    onChange={(e) => pgStanje(1, e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">3.</label>
                  <input value={(pregled.poglobljeni.premozenjsko_stanje || [])[2] || ''}
                    onChange={(e) => pgStanje(2, e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">4.</label>
                  <input value={(pregled.poglobljeni.premozenjsko_stanje || [])[3] || ''}
                    onChange={(e) => pgStanje(3, e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">5.</label>
                  <input value={(pregled.poglobljeni.premozenjsko_stanje || [])[4] || ''}
                    onChange={(e) => pgStanje(4, e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Razlogi za nameravano ali izvršeno transakcijo</label>
                  <textarea rows={2} value={pregled.poglobljeni.razlogi_transakcija || ''}
                    onChange={(e) => pgPolje('razlogi_transakcija', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Transakcije — posebnosti</label>
                  <input value={pregled.poglobljeni.transakcije_posebnosti || ''}
                    onChange={(e) => pgPolje('transakcije_posebnosti', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Poslovne aktivnosti — posebnosti</label>
                  <input value={pregled.poglobljeni.aktivnosti_posebnosti || ''}
                    onChange={(e) => pgPolje('aktivnosti_posebnosti', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                </div>
                )}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Ocena tveganja in vrsta pregleda</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium mb-1">Ugotovljeno tveganje</p>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="radio" checked={pregled.ocena_tveganja === 'obicajno'}
                      onChange={() => pregledPolje('ocena_tveganja', 'obicajno')} />
                    <span>običajno</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="radio" checked={pregled.ocena_tveganja === 'poveceno'}
                      onChange={() => pregledPolje('ocena_tveganja', 'poveceno')} />
                    <span>povečano</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="radio" checked={pregled.ocena_tveganja === 'neznatno'}
                      onChange={() => pregledPolje('ocena_tveganja', 'neznatno')} />
                    <span>neznatno</span>
                  </label>
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1">Opravljen je bil</p>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="radio" checked={pregled.vrsta_pregleda === 'obicajni'}
                      onChange={() => pregledPolje('vrsta_pregleda', 'obicajni')} />
                    <span>običajni pregled</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="radio" checked={pregled.vrsta_pregleda === 'poglobljeni'}
                      onChange={() => pregledPolje('vrsta_pregleda', 'poglobljeni')} />
                    <span>poglobljeni pregled</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="radio" checked={pregled.vrsta_pregleda === 'poenostavljeni'}
                      onChange={() => pregledPolje('vrsta_pregleda', 'poenostavljeni')} />
                    <span>poenostavljeni pregled</span>
                  </label>
                  </div>
                </div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Podpis</p>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Kraj podpisa (npr. Žiri)</label>
                  <input value={pregled.kraj_mestnik || ''}
                    onChange={(e) => pregledPolje('kraj_mestnik', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Datum pregleda</label>
                  <input type="date" value={pregled.datum_pregleda || ''}
                    onChange={(e) => pregledPolje('datum_pregleda', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Pregled opravil</label>
                  <input value={pregled.opravil_ime || ''}
                    onChange={(e) => pregledPolje('opravil_ime', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Funkcija</label>
                  <input value={pregled.opravil_funkcija || ''}
                    onChange={(e) => pregledPolje('opravil_funkcija', e.target.value)}
                    className={`${inputCls} text-sm`} />
                </div>
                </div>
                {/* [PATCH kraj-stranke v1] kraj, kjer podpise stranka */}
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Kraj podpisa stranke (npr. Bled)
                  </label>
                  <input value={pregled.kraj_stranke_mestnik || ''}
                    onChange={(e) => pregledPolje('kraj_stranke_mestnik', e.target.value)}
                    className={`${inputCls} text-sm`} />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Neobvezno. Datum podpisa stranke ostane prazen — izpolni ga stranka ob podpisu.
                  </p>
                </div>
                {pregledNapaka && (
                  <div className="text-xs text-red-600">{pregledNapaka}</div>
                )}
                <button type="button" onClick={shraniPregled}
                  disabled={pregledShranjujem}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg">
                  {pregledShranjujem ? 'Shranjujem…' : 'Shrani obrazec'}
                </button>
          </div>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-sm font-semibold">Dokumenti</h3>
              {dokNapaka && (
                <span className="text-xs text-amber-600 dark:text-amber-400 text-right">{dokNapaka}</span>
              )}
            </div>
            {DOKUMENTI.map((x) => (
              <div
                key={x.vrsta}
                className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 dark:border-gray-800 last:border-0 py-1.5"
              >
                <span className="text-gray-700 dark:text-gray-200">{x.naslov}</span>
                <div className="flex items-center gap-3 shrink-0">
                  {zadnjiDok(x.vrsta) && (
                    <button type="button"
                      onClick={() => prenesiDok(zadnjiDok(x.vrsta))}
                      className="text-xs text-blue-600 hover:underline">Prenesi</button>
                  )}
                  <button type="button"
                    onClick={() => izdelajDok(x.vrsta)}
                    disabled={izdelujem === x.vrsta || !companyId}
                    className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md">
                    {izdelujem === x.vrsta ? 'Izdelujem…' : 'Izdelaj'}
                  </button>
                </div>
              </div>
            ))}
            {dokumenti.length > 0 && (
              <p className="text-[10px] text-gray-500 pt-2">
                Izdelani dokumenti se shranijo. Ob ponovni izdelavi ostane tudi prejšnja različica.
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => (obstojeciCompanyId ? navigate('/profil') : setVeja('izbira'))}  /* [PATCH pregled-vstop v1] */
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm dark:text-white"
            >
              Nazaj
            </button>
            <button
              type="button"
              onClick={() => { setVeja('uvoz'); setKorak(2); }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
            >
              Nadaljuj na uvoz šifrantov
            </button>
          </div>
        </div>
      )}

      {/* KORAK 2 — program */}
      {korak === 2 && !opalUkaz && (  // [PATCH opal-onboarding-E v1] skrit, ko ukaz tece
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">2. Iz katerega programa uvažaš?</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => izberiProgram('vasco')}
              className={`p-6 border-2 rounded-xl text-left ${
                program === 'vasco'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-white'
                  : 'border-gray-200 dark:border-gray-700 dark:text-white'
              }`}
            >
              <div className="font-semibold">Vasco</div>
            </button>
            <button
              onClick={() => izberiProgram('opal')}
              className={`p-6 border-2 rounded-xl text-left ${
                program === 'opal'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-white'
                  : 'border-gray-200 dark:border-gray-700 dark:text-white'
              }`}
            >
              <div className="font-semibold">Opal (OpPIS)</div>
            </button>
            <button
              disabled
              className="p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-left opacity-50 cursor-not-allowed dark:text-white"
            >
              <div className="font-semibold">Pantheon</div>
              <div className="text-sm text-gray-500">kmalu</div>
            </button>
          </div>
          <button
            onClick={() => (program === 'opal' && companyId ? setOpalDialog(true) : program === 'vasco' && companyId ? setVascoDialog(true) : setKorak(3)) /* [PATCH vasco-sync-fe v1] */}  // [PATCH opal-onboarding-E v1]
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
          >
            Naprej
          </button>
        </div>
      )}

      {/* [PATCH opal-onboarding-E v1] — dialog potrditve Opalove baze + panel "uvoz v ozadju" */}
      {opalDialog && companyId && (
        <OpalOnboardingDialog
          companyId={companyId}
          companyName={naziv}
          onZapri={() => setOpalDialog(false)}
          onRocniUvoz={() => { setOpalDialog(false); setKorak(3); }}
          onUkazUstvarjen={(u: any) => { setOpalDialog(false); setOpalUkaz(u || {}); }}
        />
      )}
      {vascoDialog && companyId && (   /* [PATCH vasco-sync-fe v1] */
        <VascoOnboardingDialog
          companyId={companyId}
          companyName={naziv}
          onZapri={() => setVascoDialog(false)}
          onRocniUvoz={() => { setVascoDialog(false); setKorak(3); }}
          onUkazUstvarjen={(u: any) => { setVascoDialog(false); setOpalUkaz(u || {}); }}
        />
      )}
      {opalUkaz && (
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Uvoz iz {program === 'vasco' ? 'Vasca' : 'Opala'} teče v ozadju</h2>
          </div>
          <div className="p-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <div>Ukaz <span className="font-mono">#{opalUkaz.id ?? '—'}</span> za bazo <span className="font-semibold">{opalUkaz.opal_baza ?? opalUkaz.vasco_baza ?? '—'}</span> je ustvarjen.</div>
            <div>Agent ga pobere ob naslednjem ciklu (do 5 minut), nato strežnik uvozi šifrante in učne vzorce (nekaj minut). Lahko delaš naprej — ob koncu dobiš obvestilo, ki ga potrdiš.</div>
          </div>
          <button
            onClick={() => navigate('/dokumenti')}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
          >
            Na dokumente
          </button>
        </div>
      )}

      {/* KORAK 3 — šifranti */}
      {korak === 3 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">3. Šifranti ({program === 'opal' ? 'Opal' : 'Vasco'})</h2>
          {opalNamig}
          {sloti.filter((s) => s.skupina === 'sifranti').map(slotVrstica)}
          <div className="flex gap-2">
            <button
              onClick={() => setKorak(2)}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-xl"
            >
              Nazaj
            </button>
            <button
              onClick={() => setKorak(4)}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
            >
              Naprej
            </button>
          </div>
        </div>
      )}

      {/* KORAK 4 — knjižbe */}
      {korak === 4 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">4. Knjižbe ({program === 'opal' ? 'Opal' : 'Vasco'})</h2>
          {opalNamig}
          {sloti.filter((s) => s.skupina === 'knjizbe').map(slotVrstica)}
          <div className="flex gap-2">
            <button
              onClick={() => setKorak(3)}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-xl"
            >
              Nazaj
            </button>
            <button
              onClick={zazeniUvoz}
              disabled={nalaganje || !vsajEna}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl disabled:opacity-50"
            >
              {nalaganje ? 'Uvažam…' : 'Zaženi uvoz'}
            </button>
          </div>
        </div>
      )}

      {/* KORAK 5 — rezultat */}
      {korak === 5 && rezultat && (() => {
        const r = rezultat?.rezultat || {};
        const kartice = [
          { kljuc: 'partnerji', naziv: 'Partnerji', ikona: '👥', glavna: r.partnerji?.vstavljeni, podnaziv: 'vstavljenih', detajli: r.partnerji },
          { kljuc: 'konti', naziv: 'Kontni plan', ikona: '📊', glavna: (r.konti?.knjizni ?? 0) + (r.konti?.sinteticni ?? 0), podnaziv: 'kontov', detajli: r.konti },
          { kljuc: 'sm', naziv: 'Stroškovna mesta', ikona: '🏢', glavna: r.stroskovna_mesta?.vstavljeni, podnaziv: 'mest', detajli: r.stroskovna_mesta },
          { kljuc: 'projekti', naziv: 'Projekti', ikona: '📁', glavna: (r.projekti?.vstavljeni ?? 0) + (r.projekti?.ozivljeni ?? 0), podnaziv: 'projektov', detajli: r.projekti },
          { kljuc: 'seed', naziv: 'Učni primeri', ikona: '🧠', glavna: r.seed?.vstavljeni ?? r.seed?.seed_vstavljeni ?? r.seed?.skupine_obdelane, podnaziv: 'primerov', detajli: r.seed },
        ];
        return (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Uvoz uspešno zaključen</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {kartice.map((k) => (
                <div key={k.kljuc} className="p-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{k.naziv}</span>
                    <span className="text-xl">{k.ikona}</span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{k.glavna ?? 0}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">{k.podnaziv}</div>
                  {k.detajli && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {k.detajli.ze_obstaja != null && <span>že obstaja: {k.detajli.ze_obstaja}</span>}
                      {k.detajli.neveljavno != null && k.detajli.neveljavno > 0 && (
                        <span className="text-amber-600 dark:text-amber-400">neveljavno: {k.detajli.neveljavno}</span>
                      )}
                      {k.detajli.ozivljeni != null && k.detajli.ozivljeni > 0 && (
                        <span className="text-green-600 dark:text-green-400">oživljenih: {k.detajli.ozivljeni}</span>
                      )}
                      {k.detajli.ze_aktivni != null && k.detajli.ze_aktivni > 0 && (
                        <span>že aktivnih: {k.detajli.ze_aktivni}</span>
                      )}
                      {k.detajli.knjizni != null && <span>knjižni: {k.detajli.knjizni}</span>}
                      {k.detajli.sinteticni != null && <span>sintetični: {k.detajli.sinteticni}</span>}
                      {k.detajli.s_ddv != null && <span>z DDV stopnjo: {k.detajli.s_ddv}</span>}
                      {k.detajli.s_trr != null && <span>s TRR: {k.detajli.s_trr}</span>}
                      {k.detajli.dokumenti_obdelani != null && <span>iz dokumentov: {k.detajli.dokumenti_obdelani}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/dokumenti')}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition"
            >
              Pojdi na dokumente
            </button>
          </div>
        );
      })()}
    </div>
  );
}
